const STAFF_LAST_PATH_KEY = "biocure_staff_last_path";
const STAFF_RETURN_PATH_KEY = "biocure_staff_return_path";

const AUTH_PATH_PREFIXES = [
  "/signin",
  "/verify",
  "/mfa-verify",
  "/mfa-setup",
  "/password-reset",
  "/password-reset-otp",
  "/set-new-password",
  "/invite",
  "/invite-verify",
  "/intake/",
];

function normalizePath(path: string): string {
  if (!path.startsWith("/")) return `/${path}`;
  return path;
}

function pathnameOnly(path: string): string {
  return path.split("?")[0].split("#")[0] || "/";
}

/**
 * Paths that must never be restored as a staff "home" destination.
 * `/` is especially dangerous: the root route sends signed-in users to `/verify`,
 * which would loop with post-auth redirects that restore `/`.
 */
export function isAuthOrPublicPath(pathname: string): boolean {
  const path = pathnameOnly(pathname);
  if (path === "/") return true;
  return AUTH_PATH_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(prefix),
  );
}

function clearStoredPath(key: string): null {
  try {
    sessionStorage.removeItem(key);
  } catch {
    // ignore
  }
  return null;
}

/** Persist the current staff route so refresh and post-login flows can restore it. */
export function saveStaffLastPath(path?: string): void {
  if (typeof window === "undefined") return;
  const full = normalizePath(
    path ?? `${window.location.pathname}${window.location.search}${window.location.hash}`,
  );
  if (isAuthOrPublicPath(pathnameOnly(full))) return;
  try {
    sessionStorage.setItem(STAFF_LAST_PATH_KEY, full);
  } catch {
    // ignore
  }
}

export function peekStaffLastPath(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const stored = sessionStorage.getItem(STAFF_LAST_PATH_KEY);
    if (!stored) return null;
    if (isAuthOrPublicPath(pathnameOnly(stored))) {
      return clearStoredPath(STAFF_LAST_PATH_KEY);
    }
    return stored;
  } catch {
    return null;
  }
}

/** Save an explicit return target before forced sign-out. */
export function saveStaffReturnPath(path?: string): void {
  if (typeof window === "undefined") return;
  const full = normalizePath(
    path ?? `${window.location.pathname}${window.location.search}${window.location.hash}`,
  );
  if (isAuthOrPublicPath(pathnameOnly(full))) return;
  try {
    sessionStorage.setItem(STAFF_RETURN_PATH_KEY, full);
    sessionStorage.setItem(STAFF_LAST_PATH_KEY, full);
  } catch {
    // ignore
  }
}

/** Preferred post-auth destination; clears the one-time return path when present. */
export function consumeStaffReturnPath(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const explicit = sessionStorage.getItem(STAFF_RETURN_PATH_KEY);
    if (explicit) {
      sessionStorage.removeItem(STAFF_RETURN_PATH_KEY);
      if (isAuthOrPublicPath(pathnameOnly(explicit))) {
        return clearStoredPath(STAFF_LAST_PATH_KEY);
      }
      return explicit;
    }
    return peekStaffLastPath();
  } catch {
    return null;
  }
}

export function resolveStaffHomePath(fallback = "/dashboard"): string {
  const resolved = consumeStaffReturnPath() ?? fallback;
  // Final guard: never send staff to a bounce/auth route after verification.
  if (isAuthOrPublicPath(pathnameOnly(resolved))) {
    return fallback.startsWith("/") ? fallback : `/${fallback}`;
  }
  return resolved;
}
